import React, { type ReactNode } from 'react';

import { localeDetails } from '~/components/CmsMain/localeGlobals';
import { ContactEntity } from '~/components/Contact';
import type { CurrencyFormatterType } from '~/components/CurrencyFormatterContext';
import { shouldNotDisplayRegionSpecificField } from '~/components/IncidentConfiguration/ConfiguredFields';
import type { ConfiguredField } from '~/components/types/incident-configuration-types';
import { isoDateToUs, localTimeToViewTime } from '~/DateTimeUtils';
import { BOOL4_OPTIONS, COUNTRY_TO_STATE_MAP } from '~/Types';

type CurrencyFormatter = CurrencyFormatterType['currencyFormatter'];

// The covered-object display flags a configured field may carry. A field is selected for a given
// consumer when its matching flag is true — `include_in_management_preview` for the preview
// summary/label, `show_in_involved` for the involved-dialog read-only fields.
export type CoveredObjectFieldFlag = 'include_in_management_preview' | 'show_in_involved';

// A configured field on a covered-object type config, plus the optional display flags that aren't
// (yet) modeled on the shared ConfiguredField type.
export interface CoveredObjectConfiguredField extends ConfiguredField {
  include_in_management_preview?: boolean;
  show_in_involved?: boolean;
}

// A covered object as it appears at runtime on the policy. Configured-field values live under
// `configured_fields_values`; contact-typed fields store id + full name in dynamic
// `configurable_contact_<N>_{id,full_name}` slots addressed via the field's db_field.
export interface CoveredObject {
  id: number | string;
  type?: string;
  configured_fields_values?: Record<string, unknown>;
  [key: string]: unknown;
}

interface Policy {
  covered_objects?: CoveredObject[];
}

interface IncidentConfiguration {
  policy?: {
    covered_objects?: { id: string; configured_fields?: CoveredObjectConfiguredField[] }[];
  };
}

export const formatPreviewValue = (
  value: unknown,
  field?: Pick<CoveredObjectConfiguredField, 'type' | 'options'>,
  currencyFormatter?: CurrencyFormatter
): string => {
  if (value === undefined || value === null || value === '') return '';
  if (Array.isArray(value)) {
    // Comma-separated to match the read-only multiselect rendering in the involved dialog
    // (SelectCustomField joins option labels with ', ').
    const options = field?.options || [];
    return value.map((v) => options.find((o) => o.id === v)?.desc || v).join(', ');
  }
  switch (field?.type) {
    case 'select': {
      const match = (field.options || []).find((o) => o.id === value);
      return match?.desc || String(value);
    }
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'yes_no_na_unknown':
      return BOOL4_OPTIONS[value as keyof typeof BOOL4_OPTIONS] || String(value);
    case 'date':
      return isoDateToUs(value as string);
    case 'time':
      return localTimeToViewTime(value as string);
    case 'monetary':
      return currencyFormatter ? currencyFormatter.format(value as number) : String(value);
    case 'us_state_select': {
      // Mirror CustomField's us_state_select rendering (US/AU): when the org region has no state
      // map the dialog renders nothing, so keep the preview blank too rather than showing the raw
      // code. When the region has a map, resolve the name (falling back to the code if unmapped).
      // localeDetails is exported from a .js module typed as `null` until populated at app startup.
      const region = (localeDetails as { locale?: { region?: string } } | null)?.locale?.region;
      const statesMap = COUNTRY_TO_STATE_MAP[region as keyof typeof COUNTRY_TO_STATE_MAP];
      return statesMap ? statesMap[value as keyof typeof statesMap] || String(value) : '';
    }
    default:
      // string, multiline_string, number, make_ncic_code — render the stored value as-is.
      return String(value);
  }
};

// Shared resolver: the linked policy covered object + its active, non-location fields selected by
// `flag`. Both consumers (management preview and involved-dialog read-only fields) share the same
// id normalization, covered-object lookup, config lookup, and filter chain — only the flag differs.
export const resolveCoveredObjectFields = (
  policy: Policy | null | undefined,
  incidentConfiguration: IncidentConfiguration | null | undefined,
  coveredObjectId: number | string | null | undefined,
  flag: CoveredObjectFieldFlag
): { linkedCoveredObject: CoveredObject | null; fields: CoveredObjectConfiguredField[] } => {
  if (coveredObjectId === undefined || coveredObjectId === null || coveredObjectId === '') {
    return { linkedCoveredObject: null, fields: [] };
  }
  // Form-stored id may be a string while policy ids are numbers — normalize.
  const normalizedId = String(coveredObjectId);
  const linkedCoveredObject = (policy?.covered_objects || []).find((co) => String(co.id) === normalizedId);
  if (!linkedCoveredObject) return { linkedCoveredObject: null, fields: [] };

  const coveredObjectConfig = (incidentConfiguration?.policy?.covered_objects || []).find(
    (c) => c.id === linkedCoveredObject.type
  );
  const fields = (coveredObjectConfig?.configured_fields || []).filter(
    (f) =>
      f?.[flag] === true && f?.active !== false && f?.type !== 'location' && !shouldNotDisplayRegionSpecificField(f)
  );
  return { linkedCoveredObject, fields };
};

// Node variant: contact-typed fields render as the rich ContactEntity (matches the involved dialog).
// Contact id + name come from the configurable_contact_<N> slot on the covered-object FE schema,
// addressed via db_field.
const renderConfiguredFieldPreviewNode = (
  field: CoveredObjectConfiguredField,
  linkedCoveredObject: CoveredObject,
  currencyFormatter?: CurrencyFormatter
): ReactNode => {
  if (field?.type === 'contact') {
    const contactId = linkedCoveredObject?.[`${field.db_field}_id`];
    if (!contactId) return '';
    return (
      <ContactEntity
        contactId={contactId}
        contactDisplayName={linkedCoveredObject?.[`${field.db_field}_full_name`] || ''}
      />
    );
  }
  const value = (linkedCoveredObject?.configured_fields_values || {})[field.id];
  return formatPreviewValue(value, field, currencyFormatter);
};

// Plain-string variant: contact-typed fields render as the stored full name (labels can't host a
// ContactEntity component). Guard on the contact id exactly like the node variant so a stale/orphaned
// `_full_name` with no `_id` renders nothing in both consumers.
const formatConfiguredFieldPreviewString = (
  field: CoveredObjectConfiguredField,
  linkedCoveredObject: CoveredObject,
  currencyFormatter?: CurrencyFormatter
): string => {
  if (field?.type === 'contact') {
    const contactId = linkedCoveredObject?.[`${field.db_field}_id`];
    if (!contactId) return '';
    return String(linkedCoveredObject?.[`${field.db_field}_full_name`] || '');
  }
  const value = (linkedCoveredObject?.configured_fields_values || {})[field.id];
  return formatPreviewValue(value, field, currencyFormatter);
};

// Summary-card consumer: [{ field: <label>, value: <ReactNode> }].
export const getManagementPreviewFields = (
  policy: Policy | null | undefined,
  incidentConfiguration: IncidentConfiguration | null | undefined,
  coveredObjectId: number | string | null | undefined,
  currencyFormatter?: CurrencyFormatter
): { field: string; value: ReactNode }[] => {
  const { linkedCoveredObject, fields } = resolveCoveredObjectFields(
    policy,
    incidentConfiguration,
    coveredObjectId,
    'include_in_management_preview'
  );
  if (!linkedCoveredObject) return [];
  return fields.map((f) => ({
    field: f.desc,
    value: renderConfiguredFieldPreviewNode(f, linkedCoveredObject, currencyFormatter),
  }));
};

// Dropdown-label consumer: a plain-string suffix (e.g. " — Gold, Yes, $1500") or '' when empty.
// Composed at each call site — never folded into getCoveredObjectDisplayName.
export const getManagementPreviewLabelSuffix = (
  policy: Policy | null | undefined,
  incidentConfiguration: IncidentConfiguration | null | undefined,
  coveredObjectId: number | string | null | undefined,
  currencyFormatter?: CurrencyFormatter
): string => {
  const { linkedCoveredObject, fields } = resolveCoveredObjectFields(
    policy,
    incidentConfiguration,
    coveredObjectId,
    'include_in_management_preview'
  );
  if (!linkedCoveredObject) return '';
  const values = fields
    .map((f) => formatConfiguredFieldPreviewString(f, linkedCoveredObject, currencyFormatter))
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== '');
  return values.length ? ` — ${values.join(', ')}` : '';
};
